import { useCallback, useEffect, useState } from "react";

import {
  createFlag,
  deleteFlag,
  fetchFlags,
  toggleFlag,
  toggleFlagEnvironment,
  updateFlag,
} from "./feature-flags-api";
import type { CreateFlagInput, FeatureFlag, UpdateFlagInput } from "./feature-flags-types";

export type UpdateOutcome =
  | { readonly ok: true }
  | { readonly ok: false; readonly message: string };

export interface UseFeatureFlagsReturn {
  flags: FeatureFlag[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
  create: (input: CreateFlagInput) => Promise<FeatureFlag>;
  toggle: (key: string, enabled: boolean) => Promise<void>;
  toggleEnv: (key: string, environment: string, enabled: boolean) => Promise<void>;
  /**
   * Save a flag, reporting the outcome rather than throwing.
   *
   * It carries the API's own message on failure. The page banner also shows
   * it, but that banner sits behind the edit dialog's overlay, so a caller
   * rendering its own error needs the text rather than just a boolean. And the
   * caller has to know at all, because a follow-up write (the serve rule) must
   * not run against a flag whose save was rejected.
   */
  update: (key: string, input: UpdateFlagInput) => Promise<UpdateOutcome>;
  remove: (key: string) => Promise<void>;
}

export function useFeatureFlags(): UseFeatureFlagsReturn {
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadFlags = useCallback(async () => {
    try {
      const data = await fetchFlags();
      setFlags(data);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load feature flags");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refresh = useCallback(() => {
    setIsLoading(true);
    void loadFlags();
  }, [loadFlags]);

  useEffect(() => {
    void loadFlags();
  }, [loadFlags]);

  const create = useCallback(
    async (input: CreateFlagInput): Promise<FeatureFlag> => {
      try {
        const flag = await createFlag(input);
        setError(null);
        await loadFlags();
        return flag;
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to create flag");
        // Rethrow so the CreateFlagDialog stays open with the user's
        // input intact instead of vanishing on a transient failure.
        // The page-level error banner above the table surfaces the cause.
        throw e;
      }
    },
    [loadFlags]
  );

  const toggle = useCallback(
    async (key: string, enabled: boolean): Promise<void> => {
      try {
        await toggleFlag(key, enabled);
        setError(null);
        setFlags((prev) => prev.map((f) => (f.flagKey === key ? { ...f, enabled } : f)));
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to toggle flag");
        await loadFlags();
      }
    },
    [loadFlags]
  );

  const toggleEnv = useCallback(
    async (key: string, environment: string, enabled: boolean): Promise<void> => {
      const flag = flags.find((f) => f.flagKey === key);
      if (!flag) {
        setError(`Unknown flag "${key}"`);
        return;
      }
      try {
        const updated = await toggleFlagEnvironment(flag, environment, enabled);
        setError(null);
        setFlags((prev) => prev.map((f) => (f.flagKey === key ? updated : f)));
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to toggle environment");
        await loadFlags();
      }
    },
    [flags, loadFlags]
  );

  const update = useCallback(
    async (key: string, input: UpdateFlagInput): Promise<UpdateOutcome> => {
      try {
        const updated = await updateFlag(key, input);
        setError(null);
        setFlags((prev) => prev.map((f) => (f.flagKey === key ? updated : f)));
        return { ok: true };
      } catch (e) {
        const message = e instanceof Error ? e.message : "Failed to update flag";
        setError(message);
        await loadFlags();
        return { ok: false, message };
      }
    },
    [loadFlags]
  );

  const remove = useCallback(
    async (key: string): Promise<void> => {
      try {
        await deleteFlag(key);
        setError(null);
        setFlags((prev) => prev.filter((f) => f.flagKey !== key));
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to delete flag");
        await loadFlags();
      }
    },
    [loadFlags]
  );

  return { flags, isLoading, error, refresh, create, toggle, toggleEnv, update, remove };
}
